'use client'

import { useCallback, useMemo } from 'react'
import { type FieldValues, type UseFormReturn, useFormContext } from 'react-hook-form'

import type { AgreementFormData } from '~/app/settings/agreements/types'
import type { ProductFormData } from '~/app/settings/products/types'
import { logger } from '~/debug/logger'
import { useUserPrefs } from '~/hooks/useUserPrefs'

import { agreementDraftToForm, agreementFormToDraft, pickDraftFields, productDraftToForm, productFormToDraft } from './draftMapping'
import type {
  AgreementDraftFields,
  BillingComposerEntity,
  BillingComposerMode,
  BillingDraft,
  BillingDraftFields,
  BillingTaxGuidance,
  ProductDraftFields,
  SkippedDraftField
} from './types'

export type ComposerFormApplyResult = {
  applied: string[]
  skipped: SkippedDraftField[]
}

export type ComposerFormAdapter = {
  /** True when the page owns a react-hook-form for `entity` that drafts can be written into. */
  hasForm: boolean
  /** Live form converted to the draft-fields (API request) shape for the `billing_draft` context ref. */
  getSnapshot: () => BillingDraftFields | undefined
  /** Writes a draft (optionally narrowed to `selectedPaths`) into the form via `reset`. Null when there is no form. */
  applyDraft: (draft: BillingDraft, selectedPaths: string[] | undefined) => ComposerFormApplyResult | null
  /** Writes just the suggested tax code / behavior into a product form. */
  applyTaxGuidance: (guidance: BillingTaxGuidance) => boolean
}

/**
 * Bridges the composer to whichever wizard form is on the page. `useFormContext` returns null outside a
 * `FormProvider` (detail pages, hubs), which is how the adapter learns the page has no form.
 */
export function useComposerFormAdapter(entity: BillingComposerEntity | undefined, mode: BillingComposerMode): ComposerFormAdapter {
  const form = useFormContext<FieldValues>() as UseFormReturn<FieldValues> | null
  const { tzDayjs } = useUserPrefs()
  const hasForm = Boolean(form) && mode !== 'view' && entity !== undefined
  const isEditing = mode === 'edit'

  const getSnapshot = useCallback((): BillingDraftFields | undefined => {
    if (!form || !hasForm) return undefined
    try {
      if (entity === 'product') return productFormToDraft(form.getValues() as ProductFormData)
      if (entity === 'agreement') return agreementFormToDraft(form.getValues() as AgreementFormData)
    } catch (error) {
      logger.warn('Billing composer: could not snapshot the form', error)
    }
    return undefined
  }, [entity, form, hasForm])

  const applyDraft = useCallback(
    (draft: BillingDraft, selectedPaths: string[] | undefined): ComposerFormApplyResult | null => {
      if (!form || !hasForm || draft.entity !== entity) return null
      const fields = selectedPaths
        ? pickDraftFields(draft.fields as Record<string, unknown>, selectedPaths)
        : (draft.fields as Record<string, unknown>)

      if (entity === 'product') {
        const current = form.getValues() as ProductFormData
        const result = productDraftToForm(fields as ProductDraftFields, current, { isEditing, tzDayjs })
        form.reset(result.next, { keepDefaultValues: true })
        return { applied: result.applied, skipped: result.skipped }
      }

      const current = form.getValues() as AgreementFormData
      const result = agreementDraftToForm(fields as AgreementDraftFields, current, { isEditing })
      form.reset(result.next, { keepDefaultValues: true })
      return { applied: result.applied, skipped: result.skipped }
    },
    [entity, form, hasForm, isEditing, tzDayjs]
  )

  const applyTaxGuidance = useCallback(
    (guidance: BillingTaxGuidance): boolean => {
      if (!form || !hasForm || entity !== 'product') return false
      if (guidance.suggestedTaxCode) {
        form.setValue('taxCode', guidance.suggestedTaxCode, { shouldDirty: true, shouldValidate: true })
      }
      if (guidance.suggestedTaxBehavior) {
        form.setValue('pricing.taxBehavior', guidance.suggestedTaxBehavior, { shouldDirty: true, shouldValidate: true })
      }
      return true
    },
    [entity, form, hasForm]
  )

  return useMemo(
    () => ({ hasForm, getSnapshot, applyDraft, applyTaxGuidance }),
    [applyDraft, applyTaxGuidance, getSnapshot, hasForm]
  )
}
